import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, Check, X, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const AGENTS = [
    { key: "next-actions", label: "Next Actions" },
    { key: "schedule-review", label: "Schedule Review" },
    { key: "cost-review", label: "Cost Review" },
    { key: "procurement-review", label: "Procurement" },
    { key: "funding-review", label: "Funding" },
    { key: "risk-review", label: "Risk" },
    { key: "opportunity-review", label: "Opportunity" },
];

export function AIInsightsPanel({ projectId, defaultAgent = "next-actions" }) {
    const [agent, setAgent] = useState(defaultAgent);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(true);

    const run = async () => {
        setLoading(true); setPayload(null);
        try {
            const { data } = await api.post(`/projects/${projectId}/ai/${agent}`);
            setPayload(data);
        } catch (e) {
            const msg = e?.response?.data?.detail || "AI request failed";
            if (String(msg).includes("entitlement")) {
                setPayload({ error: "Upgrade required", explanation: "This AI agent is available on Developer, Commercial, and Enterprise plans." });
            } else {
                toast.error(msg);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => { run(); /* eslint-disable-next-line */ }, [agent, projectId]);

    return (
        <div className="border border-navy/30 bg-navy/[0.03] p-5" data-testid="ai-insights-panel">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-steel" />
                    <span className="font-medium text-sm">Forge AI Insights</span>
                    <span className="chip chip-ai">Demonstration output — verify before acting</span>
                </div>
                <button onClick={() => setOpen(!open)} data-testid="ai-toggle">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
            </div>
            {open && (
                <div className="mt-4">
                    <div className="flex flex-wrap gap-1 mb-3">
                        {AGENTS.map((a) => (
                            <button key={a.key} onClick={() => setAgent(a.key)} className={`text-[11px] px-2 py-1 border ${agent === a.key ? "border-navy bg-secondary" : "border-border"}`} data-testid={`ai-agent-${a.key}`}>{a.label}</button>
                        ))}
                    </div>
                    {loading && <div className="text-sm text-muted-foreground">Analyzing project state…</div>}
                    {payload?.error && (
                        <div className="p-4 bg-secondary/40 text-xs">
                            <div className="font-medium">{payload.error}</div>
                            <div className="text-muted-foreground mt-1">{payload.explanation}</div>
                        </div>
                    )}
                    {payload && !payload.error && (
                        <>
                            <div className="text-sm mb-3 leading-relaxed" data-testid="ai-explanation">{payload.explanation}</div>
                            <ul className="space-y-2">
                                {payload.structured_actions.map((a, i) => (
                                    <li key={i} className="border border-border p-3 text-sm" data-testid={`ai-action-${i}`}>
                                        <div className="flex items-start gap-3">
                                            <span className={`chip ${a.priority === "high" ? "chip-navy" : "chip-ai"}`}>{a.priority}</span>
                                            <div className="flex-1">
                                                <div className="font-medium">{a.title}</div>
                                                <div className="text-xs text-muted-foreground mt-0.5">{a.why}</div>
                                            </div>
                                            <div className="flex gap-1 shrink-0">
                                                <button className="text-[11px] border border-border px-2 py-1 hover:bg-secondary" data-testid={`ai-dismiss-${i}`}><X size={11} className="inline" /> Dismiss</button>
                                                <button className="text-[11px] bg-navy text-arch-white px-2 py-1 hover:opacity-90" data-testid={`ai-approve-${i}`}><Check size={11} className="inline" /> Approve</button>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                                {payload.structured_actions.length === 0 && <li className="text-sm text-muted-foreground">No recommended actions right now.</li>}
                            </ul>
                            <div className="mt-4 flex items-center justify-between text-[10px] text-muted-foreground">
                                <span>Confidence: {payload.confidence_level}%</span>
                                <span>Every approval creates records that require your explicit confirmation.</span>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

/* ==================== REPORTS TAB ==================== */
export function ReportsTab({ projectId }) {
    const [kinds, setKinds] = useState([]);
    useEffect(() => {
        (async () => {
            const { data } = await api.get(`/reports/kinds`);
            setKinds(data.kinds || []);
        })();
    }, []);

    const openPDF = async (kind) => {
        const token = localStorage.getItem("bdo_access") || "";
        const url = kind === "snapshot"
            ? `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/snapshot.pdf`
            : kind === "investor_update"
                ? `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/investor-update.pdf`
                : null;
        if (url) {
            const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await resp.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${kind}.pdf`;
            link.click();
            return;
        }
        // generic run
        try {
            const resp = await api.post(`/projects/${projectId}/reports/run`, { kind }, { responseType: "blob" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(resp.data);
            link.download = `${kind}.pdf`;
            link.click();
        } catch { toast.error("Report generation failed"); }
    };

    return (
        <div className="space-y-4" data-testid="tab-reports-content">
            <div className="overline">Report templates ({kinds.length})</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {kinds.map((k) => (
                    <div key={k.key} className="border border-border p-4" data-testid={`report-${k.key}`}>
                        <div className="font-medium text-sm">{k.label}</div>
                        <div className="text-[11px] text-muted-foreground mt-1">PDF · Generated on demand</div>
                        <button onClick={() => openPDF(k.key)} className="mt-3 w-full bg-navy text-arch-white text-xs py-1.5" data-testid={`generate-${k.key}`}>Generate</button>
                    </div>
                ))}
            </div>
            <div className="border border-amber-500/40 bg-amber-500/5 p-3 text-[11px]">
                <b>Weekly Investor Snapshot</b> — automatic Friday 08:00 UTC email delivery is available on Developer / Commercial / Enterprise plans. Enable at Settings → Notifications. Email transport is currently MOCKED (recipient list logged to audit) until RESEND_API_KEY or SENDGRID_API_KEY is provided.
            </div>
        </div>
    );
}
